import type { ColumnsType, ColumnType } from 'antd/es/table';
import type { Feature, Point } from 'geojson';
import type { TFunction } from 'i18next';
import DisplayTrapNameId from '@features/regional-overview/digital-traps/components/display-trap-name-id';
import StatusTag from '@features/regional-overview/digital-traps/components/status-tag/status-tag.component';
import type { StatusTypeEnum } from '@features/regional-overview/digital-traps/components/status-tag/status-tag.model';
import type { TrapGroup } from '@features/regional-overview/digital-traps/domain';
import type {
  AssetGroup,
  ManagementType,
  RegionalOverviewDigitalTrapsProperties,
  TrapAsset
} from 'querys/regional-overview/regional-overview.query.models';
import { createRoot } from 'react-dom/client';
import LocationCell from './location-cell';
import { NETWORK_METRICS } from './network-management.constants';
import type { NetworkTrapGroup, NetworkTrapRow } from './network-management.models';
import TrapPin from './trap-pin';

const renderTrapNameCell = (text: string, record: NetworkTrapRow) => <DisplayTrapNameId trapName={text} trapId={record.id} />;

const renderLocationCell = (_location: string, record: NetworkTrapRow) => <LocationCell record={record} />;

const renderStatusTagCell = (type: StatusTypeEnum, status: string, positiveStatus?: boolean) => (
  <StatusTag type={type} status={status} alignSelf='center' positiveStatus={positiveStatus} />
);

export const createNetworkTableColumns = (t: TFunction): ColumnsType<NetworkTrapRow> => [
  {
    title: t('pages.regional_overview.digital_traps.daily_counts.trap_name_id'),
    dataIndex: 'name',
    key: 'name',
    render: renderTrapNameCell,
    fixed: 'left',
    width: '17%',
    ellipsis: true
  },
  {
    title: t('pages.regional_overview.digital_traps.location'),
    dataIndex: 'location',
    key: 'location',
    render: renderLocationCell,
    align: 'left',
    width: '15%',
    ellipsis: true
  },
  ...NETWORK_METRICS.map(
    (metric): ColumnType<NetworkTrapRow> => ({
      title: t(metric.labelKey),
      dataIndex: metric.key,
      key: metric.key,
      render: (status: string) => renderStatusTagCell(metric.statusType, status, metric.positiveStatus),
      align: 'left',
      width: '13%',
      ellipsis: true,
      sorter: (a, b) => a[metric.key].localeCompare(b[metric.key])
    })
  )
];

const mapTrapFeatureToNetworkRow = (trap: Feature<Point, RegionalOverviewDigitalTrapsProperties>): NetworkTrapRow => {
  const coordinates = trap.geometry.coordinates;
  const location = `${coordinates[1].toFixed(4)}, ${coordinates[0].toFixed(4)}`;
  return {
    key: trap.properties.id,
    name: trap.properties.name,
    id: trap.properties.id,
    assetId: trap.properties.asset_id,
    location,
    coordinates,
    battery: trap.properties.details.battery_voltage ?? '',
    signal: trap.properties.details.signal_strength ?? '',
    pheromone: trap.properties.details.pheromone ?? '',
    tweaks: trap.properties.details.tweak ?? '',
    selfCleaning: trap.properties.details.self_cleaning ?? '',
    pestEppoCode: trap.properties.group.pest_eppo_code,
    provider: trap.properties.provider
  };
};

const mapNetworkRowToAssetGroup = (row: NetworkTrapRow): TrapAsset => {
  return {
    asset_id: row.assetId,
    provider_id: row.provider
  };
};

const mapTrapFeatureToAsset = (trap: Feature<Point, RegionalOverviewDigitalTrapsProperties>): TrapAsset => {
  return {
    asset_id: trap.properties.asset_id,
    provider_id: trap.properties.provider
  };
};

export const mapGroupedTrapsToAssetGroup = (
  groupName: string,
  trapsInGroup: Feature<Point, RegionalOverviewDigitalTrapsProperties>[]
): AssetGroup => {
  const assets = trapsInGroup.map(mapTrapFeatureToAsset);

  const firstTrap = trapsInGroup[0];
  const isCustomGroup = firstTrap.properties.group.custom_group;
  const group: AssetGroup = {
    assets,
    id: firstTrap.properties.group.id,
    name: groupName,
    organization_id: '',
    source_id: 'DIGITAL_TRAPS',
    custom_group: isCustomGroup,
    management_type: 'NAME_EDIT'
  };

  return group;
};

interface TransformGroupedFeaturesToNetworkTrapsParams {
  trapGroups: TrapGroup[];
  dynamicColumns: ColumnsType<NetworkTrapRow>;
  onGroupTitleEdit: (group: AssetGroup) => void;
}
export const transformGroupedFeaturesToNetworkTraps = ({
  trapGroups,
  dynamicColumns,
  onGroupTitleEdit
}: TransformGroupedFeaturesToNetworkTrapsParams): NetworkTrapGroup[] => {
  return trapGroups.map(group => {
    const tableData: NetworkTrapRow[] = group.trapsInGroup.map(mapTrapFeatureToNetworkRow);

    return {
      title: group.title,
      key: group.id,
      tableColumns: dynamicColumns,
      tableDataSource: tableData,
      onEditTitle: () => {
        const groupToEdit = mapGroupedTrapsToAssetGroup(group.title, group.trapsInGroup);
        onGroupTitleEdit(groupToEdit);
      }
    };
  });
};

export const renderTrapPin = () => {
  const pinContainer = document.createElement('div');
  const root = createRoot(pinContainer);
  root.render(<TrapPin />);

  return pinContainer;
};

export const mapTrapGroupToAssetGroup = (trapGroup: TrapGroup, management: ManagementType): AssetGroup => ({
  id: trapGroup.id,
  assets: trapGroup.trapsInGroup.map(mapTrapFeatureToAsset),
  name: trapGroup.title,
  organization_id: '',
  source_id: 'DIGITAL_TRAPS',
  custom_group: trapGroup.customGroup,
  management_type: management
});

export const mergeAssets = (existingGroup: AssetGroup, newAssets: TrapAsset[]): AssetGroup => {
  const mergedAssets = new Map<string, TrapAsset>();

  existingGroup.assets.forEach(asset => {
    mergedAssets.set(asset.asset_id, asset);
  });

  newAssets.forEach(asset => {
    mergedAssets.set(asset.asset_id, asset);
  });

  return {
    ...existingGroup,
    assets: Array.from(mergedAssets.values())
  };
};

export const mapSelectedTrapsToAssetGroup = (selectedTraps: NetworkTrapRow[], managementType: ManagementType) => {
  const assetGroup: AssetGroup = {
    name: '',
    assets: selectedTraps.map(mapNetworkRowToAssetGroup),
    organization_id: '',
    source_id: 'DIGITAL_TRAPS',
    management_type: managementType,
    custom_group: false
  };

  return assetGroup;
};

export const isSelectionDisabled = (record: NetworkTrapRow, selectedPestEppoCode?: string) => {
  return selectedPestEppoCode !== undefined && (record.pestEppoCode ?? '') !== selectedPestEppoCode;
};
